import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useState, useCallback } from 'react'
import { Stack } from '@sureco/design-system'
import { GridApi, GridReadyEvent } from '@ag-grid-community/core'
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { Input, Button, Modal, App } from '@surecompanies/core_components'
import EnrollmentOutstandingTable from './EnrollmentOutstandingTable'
import dayjs from 'dayjs'
import { useEnrollmentOutstanding } from '../hooks/useEnrollmentOutstanding'
import { notifyRescheduleResult } from '../hooks/useEnrollmentPeriodDates'
import { EnrollmentConfiguration } from '../hooks/useEnrollmentConfiguration'
import { Person } from 'types/person'

export default function EnrollmentOutstanding({
  id,
  enrollment,
}: {
  id: string
  enrollment: EnrollmentConfiguration
}) {
  /* --------------------------------- States --------------------------------- */
  const { queryEnrollmentOutstanding, updateEnrollmentOutstandingMutation } =
    useEnrollmentOutstanding(id)

  const {
    data,
    isLoading: isLoadingQuery,
    isRefetching,
    refetch,
  } = queryEnrollmentOutstanding(enrollment) || {}
  const { members = [], total = 0 } = data || {}

  const { mutate: updateEnrollmentOutstanding, isPending } =
    updateEnrollmentOutstandingMutation()

  const isLoading = isPending || isLoadingQuery || isRefetching
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [selectedRows, setSelectedRows] = useState<Person[]>([])
  const [searchText, setSearchText] = useState('')
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)

  /* -------------------------------- Handlers -------------------------------- */
  const onFilterTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchText(value)
      if (gridApi) {
        gridApi.setQuickFilter(value)
      }
    },
    [gridApi]
  )

  /* --------------------------------- Methods -------------------------------- */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  const handleConfirmCreate = () => {
    // If no rows selected, use all members
    const personIds = (
      selectedRows.length
        ? selectedRows.map((row) => row.employeePublicKey)
        : members.map((member) => member.employeePublicKey)
    ).filter((id): id is string => id !== undefined)
    // Update enrollment outstanding
    updateEnrollmentOutstanding(
      { personIds, enrollmentConfiguration: enrollment },
      {
        // The mutation can resolve 200 with a partial failure — inspect
        // `errors` rather than assuming every id succeeded.
        onSuccess: (result) => {
          notifyRescheduleResult(result)
          setSelectedRows([])
          setIsConfirmModalVisible(false)
        },
      }
    )
  }

  /* ---------------------------------- Doms ---------------------------------- */
  return (
    <App>
      <ContentSection loading={isLoadingQuery}>
        <Stack space={16}>
          <Stack
            direction="row"
            space={16}
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Input
              placeholder="Search by name or ID..."
              onChange={onFilterTextChange}
              prefix={<SearchOutlined />}
              style={{ width: '300px' }}
            />
            <Stack direction="row" space={8}>
              <Button
                type="primary"
                onClick={() => setIsConfirmModalVisible(true)}
                loading={isLoading}
                disabled={!members.length}
              >
                Create Outstanding Enrollments{' '}
                {selectedRows.length
                  ? `(${selectedRows.length})`
                  : members.length
                    ? '(All)'
                    : '(No data)'}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isRefetching}
              />
            </Stack>
          </Stack>
          <EnrollmentOutstandingTable
            members={members}
            total={total}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            onGridReady={onGridReady}
            quickFilterText={searchText}
          />
        </Stack>

        <Modal
          title="Create Outstanding Enrollments"
          open={isConfirmModalVisible}
          onOk={handleConfirmCreate}
          onCancel={() => setIsConfirmModalVisible(false)}
          confirmLoading={isLoading}
        >
          <div>
            This will update the following for{' '}
            {selectedRows.length ? (
              <>
                {selectedRows.length} selected{' '}
                {selectedRows.length === 1 ? 'person' : 'people'}
              </>
            ) : (
              'all people'
            )}
            :
            <ul>
              <li>
                Benefit effective date:{' '}
                {dayjs
                  .utc(enrollment.benefitEffectiveDate)
                  .format('YYYY/MM/DD')}
              </li>
              <li>
                Enrollment start date:{' '}
                {dayjs.utc(enrollment.enrollmentStartDate).format('YYYY/MM/DD')}
              </li>
              <li>
                Enrollment end date:{' '}
                {dayjs.utc(enrollment.enrollmentEndDate).format('YYYY/MM/DD')}
              </li>
            </ul>
            Are you sure you want to continue?
          </div>
        </Modal>
      </ContentSection>
    </App>
  )
}
